import { parseServerTime, serverNow } from "@/filters/DateAndTime";
import GraphRanges from "@/mixins/GraphRanges";
import { patientNamespace } from "@/store/modules/patient";
import { Patient } from "@/store/modules/patient/types";
import _ from "lodash";
import { mixins } from "vue-class-component";
import { Component } from "vue-property-decorator";
import { Getter } from "vuex-class";
import { MultiChartConfig } from "../shared/chart/MultiChartWithSharedRange";
import { SHORT_DATE_FORMAT, SHORT_DATE_WITH_YEAR_FORMAT } from "../shared/util";

const MAX_DATES_SHOWN = 6;

@Component({})
export default class O2 extends mixins(GraphRanges) {
  @Getter("graph", patientNamespace)
  graph!: any;

  @Getter("config", patientNamespace)
  config!: any;

  @Getter("atomList", patientNamespace)
  atomList!: any;

  @Getter("current", patientNamespace)
  current!: Patient;

  // list of arterial blood gas variables for display
  abgList = {
    abgFiO2: { label: "FiO<sub>2</sub>", decimals: 0, units: "%" },
    abgPaO2: { label: "PaO<sub>2</sub>", decimals: 0, units: "" },
    abgPaCO2: { label: "PaCO<sub>2</sub>", decimals: 0, units: "" },
    abgpH: { label: "pH", decimals: 2, units: "" },
    Bicarb: { label: "HCO<sub>3</sub>", decimals: 1, units: "" },
    abgBE: { label: "Base Excess", decimals: 1, units: "" },
    Lactate: { label: "Lactate", decimals: 1, units: "" },
    abgCalcium: { label: "Calcium", decimals: 1, units: "" }
  };

  get graphData() {
    return this.graph(Object.keys(this.abgList), true).map((data: any) => {
      return {
        key: data.label,
        data: data.data
          .map((d: any) => {
            const retVal: Record<string, any> = {};
            const dateKey = d.x;
            const dateValue = d.y;
            retVal[dateKey.format("YYYY-MM-DD HH:mm:ssZ[Z]")] = dateValue;
            return retVal;
          })
          .reduce((acc: any, val: any) => {
            const key = Object.keys(val)[0];
            acc[key] = val[key];
            return acc;
          })
      };
    });
  }
  get mostRecentDates() {
    if (!this.current) {
      return [];
    }
    const graphData = this.graphData;
    const dateList: Record<string, boolean> = {};
    graphData.forEach((graph: any) => {
      Object.keys(graph.data).forEach(date => {
        dateList[date] = true;
      });
    });

    const length = Object.keys(dateList).length;
    let start = length - MAX_DATES_SHOWN;
    if (start < 0) {
      start = 0;
    }
    const mostRecentDates = Object.keys(dateList)
      .sort()
      .slice(start, start + MAX_DATES_SHOWN);
    return mostRecentDates;
  }

  get tableHeaders() {
    const currentTime = serverNow();
    return this.mostRecentDates.map(d => {
      const parsedDate = parseServerTime(d);
      const format =
        parsedDate.year === currentTime.year
          ? SHORT_DATE_FORMAT
          : SHORT_DATE_WITH_YEAR_FORMAT;
      return parsedDate.format(format);
    });
  }

  get charts(): MultiChartConfig[] {
    const chartList: MultiChartConfig[] = [];

    if (this.spO2CO2Data.length) {
      chartList.push({
        title: "SpO<sub>2</sub> (%) & End Tidal CO<sub>2</sub> (mmHg)",
        data: this.spO2CO2Data,
        library: this.spO2EtCO2Config
      });
    }

    if (this.pf.length) {
      chartList.push({
        title: "P/F Ratio",
        data: this.pf,
        min: 0,
        suggestedMax: 500
      });
    }
    return chartList;
  }

  get pf() {
    return this.graph([{ key: "PFRatio", label: "P/F Ratio" }], true);
  }

  get spO2CO2Data() {
    return this.graph(
      [
        { key: "SpO2", label: "SpO₂", yAxisID: "Left" },
        {
          key: "EndTidalCO2",
          label: "End Tidal CO₂",
          yAxisID: "Right"
        }
      ],
      true
    );
  }

  get spO2CO2Label() {
    const labels: Array<string> = [];
    this.spO2CO2Data.forEach((d: any) => {
      labels.push(d.label);
    });
    return labels;
  }

  get spO2EtCO2Config() {
    const spO2Scale = {
      id: "Left",
      position: "left",
      type: "linear",
      ticks: {
        max: 100,
        min: 70,
        stepSize: 5
      }
    };

    const etCo2Scale = {
      id: "Right",
      position: "right",
      type: "linear",
      ticks: {
        min: 0,
        max: 60,
        stepSize: 10
      }
    };
    const yAxes: Array<any> = [];

    this.spO2CO2Label.forEach((label: string) => {
      switch (label) {
        case "SpO₂":
          yAxes.push(spO2Scale);
          break;
        case "End Tidal CO₂":
          yAxes.push(etCo2Scale);
          break;
      }
    });

    return {
      scales: {
        yAxes
      }
    };
  }

  get medianDisclaimer() {
    const etCo2Trend: string = _.get(this, "atomList['spO2'].trend", "15");
    const spo2Trend: string = _.get(this, "atomList['etCO2'].trend", "15");
    return (
      "*Data points displayed for " +
      this.spO2CO2Label.join(" & ") +
      " represent the median over " +
      (etCo2Trend != spo2Trend
        ? "(" + etCo2Trend + ", " + spo2Trend + ") minutes respectively"
        : etCo2Trend + " minutes") +
      " and are saved to the graph every 15 minutes."
    );
  }
}
